import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AxiosError } from "axios";
import {
  CalendarDays,
  Clock3,
  FileText,
  Fingerprint,
  Loader2,
  LogIn,
  LogOut,
  RotateCw,
  ShieldAlert,
  Upload,
  X,
} from "lucide-react";
import { clock } from "../api/rrhh";
import type { RrhhClock } from "../types/rrhh";

type Props = {
  clockData: RrhhClock;
  loading: boolean;
  onReload: () => Promise<void> | void;
};

type Feedback = {
  tone: "success" | "error" | "warning";
  text: string;
} | null;

export function TimeClockPanel({ clockData, loading, onReload }: Props) {
  const [seconds, setSeconds] = useState(clockData.seconds);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [pendingAction, setPendingAction] = useState<"entrada" | "salida" | null>(
    null,
  );
  const [justificationComment, setJustificationComment] = useState("");
  const [justificationFile, setJustificationFile] = useState<File | null>(null);
  const [justificationMessage, setJustificationMessage] = useState("");

  const isOpen = clockData.status === "abierto";  const clockBlocked = clockData.clock_blocked === true;

  useEffect(() => {
    setSeconds(clockData.seconds);
  }, [clockData.seconds, clockData.status]);

  useEffect(() => {
    if (!isOpen) return;

    const timer = window.setInterval(() => {
      setSeconds((value) => value + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isOpen]);

  const statusLabel = useMemo(() => {
    if (clockData.is_rest_day) return "Día de descanso";
    if (clockBlocked) return "Día de vacaciones o descanso";
    if (clockData.status === "abierto") return "Fichaje activo";
    if (clockData.status === "cerrado") return "Jornada cerrada";
    if (clockData.status === "autocerrado") return "Autocerrado a las 8h";
    return "Sin entrada registrada";
  }, [clockBlocked, clockData.is_rest_day, clockData.status]);

  const handleClock = async (
    action: "entrada" | "salida",
    justification?: { comentario: string; documento?: File | null },
  ) => {
    setSaving(true);
    setFeedback(null);

    try {
      const response = await clock({
        action,
        comentario: justification?.comentario,
        documento: justification?.documento,
      });

      setFeedback({
        tone: "success",
        text: response.message,
      });
      closeJustificationModal();
      await onReload();
    } catch (error) {
      if (error instanceof AxiosError) {
        const responseData = error.response?.data as
          | {
              message?: string;
              requires_justification?: boolean;
              reason?: { message?: string };
            }
          | undefined;

        if (responseData?.requires_justification) {
          setPendingAction(action);
          setJustificationMessage(
            responseData.reason?.message ??
              responseData.message ??
              "Necesitas justificar este fichaje.",
          );
          setFeedback({
            tone: "warning",
            text: "El horario requiere justificación antes de registrar el fichaje.",
          });
          return;
        }

        setFeedback({
          tone: "error",
          text:
            responseData?.message ?? "No se pudo registrar el fichaje.",
        });
      } else {
        setFeedback({
          tone: "error",
          text: "No se pudo registrar el fichaje.",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const closeJustificationModal = () => {
    if (saving) return;

    setPendingAction(null);
    setJustificationComment("");
    setJustificationFile(null);
    setJustificationMessage("");
  };

  const scheduleLabel = clockBlocked
    ? `Día de vacaciones o descanso · ${formatShortDate(clockData.today)}`
    : clockData.effective_schedule_label ??
      (clockData.schedule
        ? `${clockData.schedule.hora_inicio.slice(0, 5)} - ${clockData.schedule.hora_fin.slice(0, 5)}`
        : "Sin horario activo");

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PanelHeader
        icon={<Fingerprint size={20} />}
        title="Fichaje"
        subtitle="Registra tu jornada con control horario, tolerancias y justificación guiada."
        action={
          <button
            type="button"
            onClick={() => void onReload()}
            disabled={loading || saving}
            className="rrhh-soft-button"
          >
            <RotateCw size={15} className={loading ? "animate-spin" : ""} />
            Actualizar
          </button>
        }
      />

      <div className="grid min-h-0 flex-1 gap-5 overflow-auto p-5 xl:grid-cols-[minmax(0,1fr)_330px]">
        <section className="rrhh-users-gradient-card rrhh-soft-gradient-card flex min-h-[420px] flex-col items-center justify-center p-6 text-center">
          <div className="mb-4 flex flex-wrap items-center justify-center gap-3">
            <Badge icon={<CalendarDays size={14} />} tone="neutral">
              {formatLongDate(clockData.today)}
            </Badge>

            <Badge
              icon={<Clock3 size={14} />}
              tone={clockBlocked ? "warning" : isOpen ? "success" : clockData.status === "autocerrado" ? "warning" : "neutral"}
            >
              {statusLabel}
            </Badge>
          </div>

          <div className="rrhh-clock-glow pointer-events-none" />
          <div className="rrhh-clock-display">{formatSeconds(seconds)}</div>

          <div className="mt-6 grid w-full max-w-[520px] grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => void handleClock("entrada")}
              disabled={saving || clockBlocked || isOpen || clockData.status === "cerrado"}
              className="rrhh-primary-button"
            >
              {saving && pendingAction === null ? (
                <Loader2 size={17} className="animate-spin" />
              ) : (
                <LogIn size={17} />
              )}
              Entrada
            </button>

            <button
              type="button"
              onClick={() => void handleClock("salida")}
              disabled={saving || clockBlocked || !isOpen}
              className="rrhh-secondary-button"
            >
              {saving && pendingAction === null ? (
                <Loader2 size={17} className="animate-spin" />
              ) : (
                <LogOut size={17} />
              )}
              Salida
            </button>
          </div>

          {clockBlocked && (
            <FeedbackBox
              feedback={{
                tone: "warning",
                text:
                  clockData.clock_block_reason ??
                  "Hoy es día de vacaciones o descanso. No puedes fichar.",
              }}
              className="mt-5 max-w-[560px]"
            />
          )}

          {feedback && (
            <FeedbackBox feedback={feedback} className="mt-5 max-w-[560px]" />
          )}

          {clockData.entry_time && (
            <div className="mt-5 grid w-full max-w-[520px] grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <InfoCard label="Entrada" value={clockData.entry_time.slice(0, 5)} />
              <InfoCard
                label="Salida"
                value={clockData.exit_time ? clockData.exit_time.slice(0, 5) : "—"}
              />
            </div>
          )}
        </section>

        <aside className="rrhh-users-gradient-card rrhh-soft-gradient-card flex flex-col gap-4 p-5">
          <div className="rounded-[28px] border border-[#edf1f3] bg-white/85 p-4 shadow-[0_10px_28px_rgba(15,23,42,.045)]">
            <div className="text-xs font-black uppercase tracking-[0.14em] text-emerald-700">
              Fecha de hoy
            </div>
            <div className="mt-2 text-2xl font-black tracking-tight text-slate-950">
              {formatLongDate(clockData.today)}
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-500">
              Visible siempre para validar el fichaje del día.
            </div>
          </div>

          <div className="rounded-[28px] border border-[#edf1f3] bg-[linear-gradient(180deg,#ffffff_0%,#f8fcfa_100%)] p-4 shadow-[0_10px_28px_rgba(15,23,42,.045)]">
            <div className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">
              Horario activo
            </div>
            <div className="mt-2 text-xl font-black text-slate-950">
              {scheduleLabel}
            </div>
            <div className="mt-2 text-sm font-semibold text-slate-500">
              {clockBlocked
                ? clockData.is_rest_day
                  ? `No tienes horario activo porque hoy es día de descanso de la plantilla${clockData.rest_day_name ? `: ${clockData.rest_day_name}` : ""}.`
                  : "No tienes horario activo este día porque la jornada está cubierta por vacaciones aprobadas."
                : clockData.schedule
                  ? `Tolerancia de ${clockData.schedule.tolerancia_minutos} minuto(s). Vigente desde ${formatShortDate(clockData.schedule.vigente_desde)}.`
                  : "Si fichas hoy, el sistema pedirá justificación por ausencia de horario vigente."}
            </div>
          </div>

          <div className="rounded-[28px] border border-[#efe6cf] bg-[linear-gradient(180deg,#fffdf7_0%,#fff8eb_100%)] p-4 text-left shadow-[0_10px_28px_rgba(245,158,11,.08)]">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-amber-700">
              <ShieldAlert size={13} />
              Validaciones
            </div>

            <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-600">
              <li>Se valida día, horario activo, vigencia y tolerancia.</li>
              <li>Los fichajes fuera de horario requieren comentario obligatorio.</li>
              <li>Puedes adjuntar un documento justificativo antes de registrar.</li>
            </ul>
          </div>
        </aside>
      </div>

      {pendingAction && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-950/30 p-4 backdrop-blur-md">
          <div className="dashboard-scale-in w-full max-w-2xl overflow-hidden rounded-[38px] border border-[#d7e5df] bg-white shadow-[0_44px_120px_rgba(15,23,42,.22)]">
            <div className="border-b border-[#edf1f3] bg-[linear-gradient(135deg,#ffffff,#f4fbf8)] px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <span className="grid h-14 w-14 place-items-center rounded-3xl border border-amber-100 bg-amber-50 text-amber-700 shadow-[0_12px_28px_rgba(245,158,11,.12)]">
                    <ShieldAlert size={24} />
                  </span>

                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.16em] text-amber-700">
                      Justificación obligatoria
                    </div>
                    <h3 className="mt-1 text-2xl font-black tracking-tight text-slate-950">
                      Fichaje fuera de horario
                    </h3>
                    <p className="mt-1 text-sm font-semibold text-slate-500">
                      {justificationMessage}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={closeJustificationModal}
                  className="grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-50 hover:text-slate-900"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="space-y-5 p-6">
              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Comentario
                </span>
                <textarea
                  value={justificationComment}
                  onChange={(event) => setJustificationComment(event.target.value)}
                  className="field-control min-h-[140px]"
                  placeholder="Explica por qué este fichaje queda fuera del horario asignado."
                />
              </label>

              <label className="group flex cursor-pointer flex-col gap-3 rounded-[28px] border border-dashed border-emerald-100 bg-emerald-50/55 p-5 shadow-[0_10px_26px_rgba(16,185,129,.055)] transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-200 hover:bg-emerald-50">
                <span className="inline-flex items-center gap-2 text-sm font-black text-emerald-700">
                  <span className="grid h-10 w-10 place-items-center rounded-2xl border border-emerald-100 bg-white text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.07)]">
                    <Upload size={16} />
                  </span>
                  Adjuntar documento
                </span>

                <span className="text-sm font-semibold text-slate-500">
                  Opcional. PDF, imagen o documento de apoyo.
                </span>

                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
                  className="hidden"
                  onChange={(event) =>
                    setJustificationFile(event.target.files?.[0] ?? null)
                  }
                />

                {justificationFile && (
                  <span className="inline-flex w-fit items-center gap-2 rounded-full border border-white bg-white px-3 py-1.5 text-xs font-black text-slate-600 shadow-[0_8px_20px_rgba(15,23,42,.045)]">
                    <FileText size={12} className="text-emerald-600" />
                    {justificationFile.name}
                  </span>
                )}
              </label>
            </div>

            <div className="flex justify-end gap-3 border-t border-[#edf1f3] bg-slate-50/80 px-6 py-5">
              <button
                type="button"
                onClick={closeJustificationModal}
                disabled={saving}
                className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-600 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-60"
              >
                Cancelar
              </button>

              <button
                type="button"
                disabled={saving || !justificationComment.trim()}
                onClick={() =>
                  void handleClock(pendingAction, {
                    comentario: justificationComment,
                    documento: justificationFile,
                  })
                }
                className="rrhh-primary-button rounded-2xl px-5 py-3 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <ShieldAlert size={16} />
                )}
                Guardar justificación
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PanelHeader({
  icon,
  title,
  subtitle,
  action,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  action?: ReactNode;
}) {
  return (
    <div className="rrhh-panel-header">
      <div className="relative flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="rrhh-panel-icon">{icon}</span>

          <div>
            <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-600">
              Jornada laboral
            </div>

            <h2 className="mt-1 text-2xl font-black tracking-tight text-slate-950">
              {title}
            </h2>

            <p className="mt-1 text-sm font-semibold leading-6 text-slate-500">
              {subtitle}
            </p>
          </div>
        </div>

        {action}
      </div>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="group relative overflow-hidden rounded-[28px] border border-[#e7ecef] bg-white/92 px-6 py-5 shadow-[0_14px_44px_rgba(15,23,42,.055)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-emerald-200 hover:shadow-[0_24px_60px_rgba(16,185,129,.12)]">
      <div className="absolute right-[-50px] top-[-50px] h-28 w-28 rounded-full bg-emerald-200/30 blur-3xl opacity-0 transition duration-300 group-hover:opacity-100" />

      <div className="relative">
        <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
          {label}
        </div>

        <div className="mt-2 text-lg font-black tracking-tight text-slate-900">
          {value}
        </div>
      </div>
    </div>
  );
}

function Badge({
  children,
  icon,
  tone,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  tone: "neutral" | "success" | "warning";
}) {
  const toneClass =
    tone === "success"
      ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
      : tone === "warning"
        ? "border border-amber-200 bg-amber-50 text-amber-700"
        : "border border-slate-200 bg-white text-slate-600";

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-black shadow-[0_10px_24px_rgba(15,23,42,.06)] ${toneClass}`}
    >
      {icon}
      {children}
    </div>
  );
}

function FeedbackBox({
  feedback,
  className = "",
}: {
  feedback: Feedback;
  className?: string;
}) {
  if (!feedback) return null;

  const toneClass =
    feedback.tone === "success"
      ? "border-emerald-100 bg-emerald-50 text-emerald-700"
      : feedback.tone === "warning"
        ? "border-amber-100 bg-amber-50 text-amber-700"
        : "border-rose-100 bg-rose-50 text-rose-600";

  return (
    <div
      className={`${className} rounded-2xl border px-4 py-3 text-sm font-bold shadow-[0_8px_22px_rgba(15,23,42,.04)] ${toneClass}`}
    >
      {feedback.text}
    </div>
  );
}

function formatSeconds(total: number) {
  const safeTotal = Math.floor(Number(total) || 0);
  const h = String(Math.floor(safeTotal / 3600)).padStart(2, "0");
  const m = String(Math.floor((safeTotal % 3600) / 60)).padStart(2, "0");
  const s = String(safeTotal % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function formatLongDate(value: string) {
  return new Date(`${value}T12:00:00`).toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatShortDate(value: string) {
  return new Date(`${value}T12:00:00`).toLocaleDateString("es-ES");
}
